/**
 * Standard Indian City Classification (7th Central Pay Commission / HRA X, Y, Z categories).
 * Tier 1 (X category): the 8 metropolitan cities, plus `New Delhi`, which people type as often as
 * `Delhi`.
 * Tier 2 (Y category): the prominent commercial and state cities. Combined official names
 * ("Durg-Bhilai Nagar", "Hubli-Dharwad", "Vasai-Virar") are listed as each of their cities, because
 * a traveller types one of them.
 * Tier 3 (Z category): every other city defaults to Tier 3 without an explicit row.
 *
 * ALIASES. The lookup is exact after `normalizeCityKey` (NFKC, trimmed, single spaces, lower case),
 * so each spelling people actually type is its own entry: `Bangalore` next to `Bengaluru`,
 * `Bombay`, `Calcutta`, `Madras`, `Mysuru`, `Cochin`, `Trivandrum`, `Vizag`, `Baroda` and so on.
 * An alias carries the same tier as the city it stands for.
 *
 * The list is the DEFAULT for an organisation with no `travel_city_tiers` rows for a country: the
 * API falls back to it (`INDIA_CITY_TIER_BY_KEY`), and the dev seed inserts it. An organisation's
 * own rows for a country always replace it.
 */

import type { TravelCityTier } from '../enums.js';
import { normalizeCityKey } from './overlap.js';

export interface SeedCityTier {
  city: string;
  tier: TravelCityTier;
}

export const RAW_INDIA_TIER_1_CITIES = [
  'Ahmedabad',
  'Bengaluru',
  'Bangalore', // alias of Bengaluru
  'Chennai',
  'Madras', // alias of Chennai
  'Delhi',
  'New Delhi',
  'Hyderabad',
  'Kolkata',
  'Calcutta', // alias of Kolkata
  'Mumbai',
  'Bombay', // alias of Mumbai
  'Pune',
] as const;

export const RAW_INDIA_TIER_2_CITIES = [
  'Agra',
  'Ajmer',
  'Aligarh',
  'Amravati',
  'Amritsar',
  'Asansol',
  'Aurangabad',
  'Chhatrapati Sambhajinagar', // renamed Aurangabad
  'Bareilly',
  'Belgaum',
  'Belagavi', // alias of Belgaum
  'Bhavnagar',
  'Bhiwandi',
  'Bhopal',
  'Bhubaneswar',
  'Bhubaneshwar', // alternate spelling
  'Bikaner',
  'Chandigarh',
  'Coimbatore',
  'Kovai', // alias of Coimbatore
  'Cuttack',
  'Dehradun',
  'Dhanbad',
  'Durg', // Durg-Bhilai Nagar, listed as each city
  'Bhilai',
  'Bhilai Nagar',
  'Durg-Bhilai Nagar',
  'Durgapur',
  'Erode',
  'Faridabad',
  'Firozabad',
  'Ghaziabad',
  'Gorakhpur',
  'Greater Noida',
  'Gulbarga',
  'Kalaburagi', // alias of Gulbarga
  'Guntur',
  'Gurgaon',
  'Gurugram',
  'Gwalior',
  'Hubli', // Hubli-Dharwad, listed as each city
  'Hubballi',
  'Dharwad',
  'Hubli-Dharwad',
  'Indore',
  'Jabalpur',
  'Jaipur',
  'Jalandhar',
  'Jammu',
  'Jamnagar',
  'Jamshedpur',
  'Jhansi',
  'Jodhpur',
  'Kakinada',
  'Kannur',
  'Kanpur',
  'Kochi',
  'Cochin', // alias of Kochi
  'Kollam',
  'Kota',
  'Kozhikode',
  'Calicut', // alias of Kozhikode
  'Kurnool',
  'Lucknow',
  'Ludhiana',
  'Madurai',
  'Malappuram',
  'Mangalore',
  'Mangaluru', // alias of Mangalore
  'Mathura',
  'Meerut',
  'Moradabad',
  'Mysore',
  'Mysuru', // alias of Mysore
  'Nagpur',
  'Nanded',
  'Nashik',
  'Navi Mumbai',
  'Nellore',
  'Noida',
  'Palakkad',
  'Patna',
  'Puducherry',
  'Pondicherry', // alias of Puducherry
  'Raipur',
  'Rajahmundry',
  'Rajkot',
  'Ranchi',
  'Rourkela',
  'Salem',
  'Sangli',
  'Siliguri',
  'Solapur',
  'Srinagar',
  'Surat',
  'Thane',
  'Thiruvananthapuram',
  'Trivandrum', // alias of Thiruvananthapuram
  'Thrissur',
  'Trichur', // alias of Thrissur
  'Tiruchirappalli',
  'Trichy', // alias of Tiruchirappalli
  'Tiruchirapalli',
  'Tirunelveli',
  'Tiruppur',
  'Ujjain',
  'Vadodara',
  'Baroda', // alias of Vadodara
  'Varanasi',
  'Benares', // alias of Varanasi
  'Banaras',
  'Vasai', // Vasai-Virar, listed as each city
  'Virar',
  'Vasai-Virar',
  'Vijayawada',
  'Visakhapatnam',
  'Vizag', // alias of Visakhapatnam
  'Vishakhapatnam',
  'Warangal',
] as const;

/**
 * Deduplicated, normalized Indian city tier list: the seed for a dev database and the API's
 * fallback for an organisation with no tier rows of its own for India.
 */
export const INDIA_CITY_TIERS: readonly SeedCityTier[] = (() => {
  const result: SeedCityTier[] = [];
  const seen = new Set<string>();

  for (const name of RAW_INDIA_TIER_1_CITIES) {
    const key = normalizeCityKey(name);
    if (!seen.has(key)) {
      seen.add(key);
      result.push({ city: key, tier: 'tier_1' });
    }
  }

  for (const name of RAW_INDIA_TIER_2_CITIES) {
    const key = normalizeCityKey(name);
    if (!seen.has(key)) {
      seen.add(key);
      result.push({ city: key, tier: 'tier_2' });
    }
  }

  return result;
})();

/** `normalizeCityKey(city)` -> tier for every entry of `INDIA_CITY_TIERS`; exact lookups only. */
export const INDIA_CITY_TIER_BY_KEY: ReadonlyMap<string, TravelCityTier> = new Map(
  INDIA_CITY_TIERS.map((row) => [row.city, row.tier] as const),
);
